import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import PatternMap from "./PatternMap";
import type { DrillPattern, HoleCompletionStatus } from "@/types/pattern";
import type { ShiftLog } from "@/types/drilling";

interface PatternProgressProps {
  patterns: DrillPattern[];
  drillingLogs: ShiftLog[];
}

export default function PatternProgress({ patterns, drillingLogs }: PatternProgressProps) {
  const [selectedPatternId, setSelectedPatternId] = useState<string>(
    patterns[0]?.id ?? ""
  );

  const selectedPattern = useMemo(
    () => patterns.find((p) => p.id === selectedPatternId) ?? null,
    [patterns, selectedPatternId]
  );

  // Merge completions from the pattern record + any shift logs that reference it
  const mergedCompletions = useMemo(() => {
    if (!selectedPattern) return [];

    const completionMap = new Map<string, { status: HoleCompletionStatus; operator: string; actualDepth?: number; date?: string }>();

    // Pattern's own completions
    for (const c of selectedPattern.completions) {
      completionMap.set(c.holeId, {
        status: c.status,
        operator: c.operatorName,
        actualDepth: c.actualDepth,
        date: c.completedAt,
      });
    }

    // From shift logs
    for (const log of drillingLogs) {
      for (const hole of log.drillHoles) {
        if (hole.sourcePatternId === selectedPattern.id && hole.sourceHoleId) {
          const mapped = hole.status === "abandoned" ? "skipped" : hole.status;
          completionMap.set(hole.sourceHoleId, {
            status: mapped as HoleCompletionStatus,
            operator: log.shiftDetails?.operatorName ?? "",
            actualDepth: hole.depth,
            date: log.shiftDetails?.date,
          });
        }
      }
    }

    return Array.from(completionMap.entries()).map(([holeId, data]) => ({
      holeId,
      status: data.status,
      shiftId: "",
      operatorName: data.operator,
      rigId: "",
      actualDepth: data.actualDepth,
      completedAt: data.date,
    }));
  }, [selectedPattern, drillingLogs]);

  const stats = useMemo(() => {
    if (!selectedPattern) return null;
    const counts = { completed: 0, inProgress: 0, skipped: 0, redrilled: 0, pending: 0 };
    const statusMap = new Map<string, HoleCompletionStatus>();
    for (const c of mergedCompletions) statusMap.set(c.holeId, c.status);

    for (const hole of selectedPattern.holes) {
      const s = statusMap.get(hole.holeId) ?? "pending";
      if (s === "completed") counts.completed++;
      else if (s === "in-progress") counts.inProgress++;
      else if (s === "skipped") counts.skipped++;
      else if (s === "redrilled") counts.redrilled++;
      else counts.pending++;
    }

    const pct = selectedPattern.holes.length > 0
      ? Math.round((counts.completed / selectedPattern.holes.length) * 100)
      : 0;

    return { ...counts, total: selectedPattern.holes.length, percent: pct };
  }, [selectedPattern, mergedCompletions]);

  // Operator contribution
  const operatorStats = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of mergedCompletions) {
      if (c.status === "completed" && c.operatorName) {
        map.set(c.operatorName, (map.get(c.operatorName) ?? 0) + 1);
      }
    }
    return Array.from(map.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [mergedCompletions]);

  if (patterns.length === 0) return null;

  return (
    <div className="space-y-4 mt-6">
      {/* Pattern selector */}
      <div className="flex items-center gap-3">
        <h3 className="text-lg font-semibold">Pattern Progress</h3>
        {patterns.length > 1 && (
          <Select value={selectedPatternId} onValueChange={setSelectedPatternId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {patterns.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.patternId} ({p.totalHoles.toLocaleString()} holes)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {selectedPattern && stats && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: "Total", value: stats.total, color: "text-foreground" },
              { label: "Completed", value: stats.completed, color: "text-green-500" },
              { label: "Pending", value: stats.pending, color: "text-muted-foreground" },
              { label: "Skipped", value: stats.skipped, color: "text-blue-400" },
              { label: "Redrilled", value: stats.redrilled, color: "text-red-500" },
            ].map(({ label, value, color }) => (
              <Card key={label} className="bg-card border-border">
                <CardContent className="py-3 px-4">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={`text-xl font-bold ${color}`}>{value.toLocaleString()}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Progress bar */}
          <div className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {stats.completed.toLocaleString()} of {stats.total.toLocaleString()} holes completed
              </span>
              <span className="font-bold text-primary">{stats.percent}%</span>
            </div>
            <Progress value={stats.percent} className="h-3" />
          </div>

          {/* Pattern map */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {selectedPattern.patternId} — Hole Map
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PatternMap
                holes={selectedPattern.holes}
                completions={mergedCompletions}
              />
            </CardContent>
          </Card>

          {/* Hole type breakdown */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Production", count: selectedPattern.productionHoles },
              { label: "Batter", count: selectedPattern.batterHoles },
              { label: "Buffer", count: selectedPattern.bufferHoles },
              { label: "T-Row", count: selectedPattern.tRowHoles },
            ].map(({ label, count }) =>
              count > 0 ? (
                <Card key={label} className="bg-card border-border">
                  <CardContent className="py-2 px-3 text-center">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="font-bold">{count.toLocaleString()}</p>
                  </CardContent>
                </Card>
              ) : null
            )}
          </div>

          {/* Operator contribution */}
          {operatorStats.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Operator Contribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {operatorStats.slice(0, 10).map(({ name, count }) => (
                    <div key={name} className="flex items-center justify-between text-sm">
                      <span>{name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24">
                          <Progress
                            value={stats.total > 0 ? (count / stats.total) * 100 : 0}
                            className="h-2"
                          />
                        </div>
                        <span className="font-mono text-xs text-muted-foreground w-12 text-right">
                          {count} holes
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
